import * as React from 'react';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  Input,
  OutlinedInput,
  FilledInput,
  MenuItem,
  ListItemText,
  Select as MatSelect,
  Checkbox,
  SelectChangeEvent,
  SxProps,
  Theme
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useSafeId } from '../../hooks';
import { SelectProps as MatSelectProps } from '../../components/Select';
import { archerConstants } from '../../utils';

const styles: Record<string, SxProps<Theme>> = {
  labelStyleHidden: {
    display: 'none'
  },
  selected: {
    '&:hover': {
      backgroundColor: archerConstants.archerHover
    }
  }
};

type MarginType = 'dense' | 'normal' | 'none';

interface MenuProps {
  ref?: React.RefObject<HTMLDivElement>;
}

interface OptionItem {
  name: string;
  id: string;
}

export interface CheckboxMultiSelectProps {
  options: OptionItem[];
  inputLabel?: string;
  helperText?: string;
  required?: boolean;
  value?: unknown;
  input?: React.ReactNode;
  onChange?: (event: SelectChangeEvent<unknown>) => void;
  onOpen?: (event: any) => void;
  onClose?: (event: any) => void;
  margin?: MarginType;
  fullWidth?: boolean;
  helperTextEnabled?: boolean;
  disabled?: boolean;
  menuProps?: MenuProps;
  showLabel?: boolean;
  variant?: MatSelectProps['variant'];
  error?: boolean;
  id?: string;
  placeholder?: string;
  readonly?: boolean;
  autoFocus?: boolean;
  sx?: SxProps<Theme>;
}

const CheckboxMultiSelect: React.FunctionComponent<CheckboxMultiSelectProps> = ({
  inputLabel = 'select-label',
  helperText,
  margin,
  fullWidth = true,
  helperTextEnabled,
  options,
  required,
  value,
  onOpen,
  onClose,
  onChange,
  disabled,
  menuProps,
  variant = 'filled',
  showLabel = true,
  error,
  id,
  readonly = false,
  autoFocus = false,
  sx
}) => {
  const { labelStyleHidden, selected } = styles;
  const safeId = useSafeId(id);
  const labelId = useSafeId();

  return (
    <FormControl margin={margin} fullWidth={fullWidth} disabled={disabled} variant={variant} className="FormControl-Select" error={error}>
      <InputLabel id={labelId} sx={!showLabel ? labelStyleHidden : undefined} required={required}>
        {inputLabel}
      </InputLabel>
      <MatSelect
        labelId={labelId}
        id={safeId}
        value={value}
        multiple
        onChange={onChange}
        input={variant === 'filled' ? <FilledInput /> : variant === 'standard' ? <Input /> : <OutlinedInput />}
        renderValue={value =>
          (options as OptionItem[])
            .filter(option => (value as string[]).includes(option.id))
            .map(option => option.name)
            .join(', ')
        }
        MenuProps={menuProps}
        IconComponent={ExpandMore}
        className="Input-Select"
        displayEmpty
        onOpen={onOpen}
        onClose={onClose}
        readOnly={readonly}
        inputProps={{ readOnly: readonly, autoFocus }}
        sx={sx}
      >
        {options.map((option: OptionItem) => {
          return (
            <MenuItem value={option.id} key={option.name} aria-label={option.name} sx={selected}>
              <Checkbox checked={(value as string[]) && (value as string[]).indexOf(option.id) > -1} />
              <ListItemText primary={option.name} />
            </MenuItem>
          );
        })}
      </MatSelect>
      {helperTextEnabled && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

CheckboxMultiSelect.displayName = 'CheckboxMultiSelect';

export { CheckboxMultiSelect };
